import { Row, Col, Card, Typography, DatePicker, Tag } from "antd";
import { useEffect, useState } from "react";
import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, DollarOutlined, WalletOutlined } from "@ant-design/icons";
import StatCard from "./StatCard";
import BarChart from './BarChart';
import PieChart from './PieChart';
import moment from "moment";
import { motion } from "framer-motion";
import axios from "axios";

function DiagonalMotionDiv(props) {
  return (
    <motion.div animate={{ x: [-50, 0], y: [-250, 0] }} transition={{ delay: props.delay, duration: 0.35, ease: "easeInOut" }}>
      {props.children}
    </motion.div>
  );
}

export default function Stats() {
  const { RangePicker } = DatePicker;

  const [dateRange, setDateRange] = useState([moment().subtract(1, "month"), moment()]);
  const [totalRegistrado, setTotalRegistrado] = useState(0);
  const [totalDictaminado, setTotalDictaminado] = useState(0);
  const [uvieSummary, setUvieSummary] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const costResponse = await axios.get(`${process.env.REACT_APP_API_URL}/estadisticas/verificaciones/costos`, {
          params: {
            rango1: moment(dateRange[0]).format("YYYY-MM-DD"),
            rango2: moment(dateRange[1]).format("YYYY-MM-DD"),
          },
        });
        setTotalRegistrado(costResponse.data[0]?.MontoTotal ?? 0);

        const folioResponse = await axios.get(`${process.env.REACT_APP_API_URL}/estadisticas/verificaciones/folios`, {
          params: {
            rango1: moment(dateRange[0]).format("YYYY-MM-DD"),
            rango2: moment(dateRange[1]).format("YYYY-MM-DD"),
          },
        });
        setTotalDictaminado(folioResponse.data[0]?.MontoFoliadoTotal ?? 0);

        const uvieResponse = await axios.get(`${process.env.REACT_APP_API_URL}/estadisticas/uvies`);
        setUvieSummary(uvieResponse.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [dateRange]);

  const columns = [
    {
      title: "NumeroUvie",
      dataIndex: "NumeroUvie",
      key: "NumeroUvie",
    },
    {
      title: "Nombre Completo",
      dataIndex: "NombreCompleto",
      key: "NombreCompleto",
    },
    {
      title: "TotalVerificaciones",
      dataIndex: "TotalVerificaciones",
      key: "TotalVerificaciones",
      render: (verifications) => {
        return verifications > 0 ? verifications : <Tag color="red">No tiene</Tag>;
      },
    },
    {
      title: "TotalMonto",
      dataIndex: "TotalMonto",
      key: "TotalMonto",
      responsive: ["lg"],
      render: (income) => {
        return income > 0 ? income.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",") : <Tag color="red">No tiene</Tag>;
      },
    },
    {
      title: "PromedioMonto",
      dataIndex: "PromedioMonto",
      key: "PromedioMonto",
      responsive: ["lg"],
      render: (average) => {
        return average > 0 ? average.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",") : <Tag color="red">No tiene</Tag>;
      },
    },
  ];

  const barData = {
    labels: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
    datasets: [
      {
        label: 'Ingresos',
        data: [12000, 19000, 30000, 50000, 20000, 30000, 45000, 32000, 28000, 48000, 39000, 52000],
        backgroundColor: 'rgba(24, 144, 255, 0.6)',
      },
    ],
  };

  const barOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Ingresos Mensuales',
      },
    },
  };

  const pieData = {
    labels: ['Cliente A', 'Cliente B', 'Cliente C', 'Cliente D', 'Cliente E'],
    datasets: [
      {
        data: [300, 50, 100, 80, 120],
        backgroundColor: [
          'rgba(255, 99, 132, 0.8)',
          'rgba(54, 162, 235, 0.8)',
          'rgba(255, 206, 86, 0.8)',
          'rgba(75, 192, 192, 0.8)',
          'rgba(153, 102, 255, 0.8)',
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Top 5 Clientes',
      },
    },
  };

  return (
    <div>
      <Row>
        <Col span={12}>
          <Typography.Title level={3} className="fancy-titles" type="secondary">
            Estadísticas
          </Typography.Title>
        </Col>
        <Col span={12} style={{ textAlign: "right" }}>
          <RangePicker
            defaultValue={dateRange}
            format={"DD/MM/YYYY"}
            bordered={false}
            onChange={(dates) => {
              setDateRange(dates);
            }}
          />
        </Col>
      </Row>
      <Row gutter={[60, 8]}>
        <Col xl={8} lg={12} md={12} sm={24} xs={24}>
          <DiagonalMotionDiv delay={0.1}>
            <Card className="card-stats">
              <StatCard
                title="Emitidas"
                value={125}
                color="#52c41a"
                icon={<CheckCircleOutlined style={{ fontSize: "24px", color: "#52c41a" }} />}
              />
            </Card>
          </DiagonalMotionDiv>
        </Col>
        <Col xl={8} lg={12} md={12} sm={24} xs={24}>
          <DiagonalMotionDiv delay={0.2}>
            <Card className="card-stats">
              <StatCard
                title="No emitidas"
                value={32}
                color="#faad14"
                icon={<ClockCircleOutlined style={{ fontSize: "24px", color: "#faad14" }} />}
              />
            </Card>
          </DiagonalMotionDiv>
        </Col>
        <Col xl={8} lg={12} md={12} sm={24} xs={24}>
          <DiagonalMotionDiv delay={0.4}>
            <Card className="card-stats">
              <StatCard
                title="Canceladas"
                value={15}
                color="#f5222d"
                icon={<CloseCircleOutlined style={{ fontSize: "24px", color: "#f5222d" }} />}
              />
            </Card>
          </DiagonalMotionDiv>
        </Col>
      </Row>
      <Row gutter={[60, 8]} style={{ marginTop: "20px" }}>
        <Col xl={8} lg={12} md={12} sm={24} xs={24}>
          <DiagonalMotionDiv delay={0.1}>
            <Card className="card-stats">
              <StatCard
                title="Ingresos"
                value={150000}
                color="#52c41a"
                prefix="$"
                icon={<DollarOutlined style={{ fontSize: "24px", color: "#52c41a" }} />}
              />
            </Card>
          </DiagonalMotionDiv>
        </Col>
        <Col xl={8} lg={12} md={12} sm={24} xs={24}>
          <DiagonalMotionDiv delay={0.2}>
            <Card className="card-stats">
              <StatCard
                title="Por cobrar"
                value={25000}
                color="#faad14"
                prefix="$"
                icon={<WalletOutlined style={{ fontSize: "24px", color: "#faad14" }} />}
              />
            </Card>
          </DiagonalMotionDiv>
        </Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginTop: "20px" }}>
        <Col xs={{ span: 24 }} lg={{ span: 12 }}>
          <Card bordered={false} className="card-stats" title="Ingresos mensuales">
            <BarChart data={barData} options={barOptions} />
          </Card>
        </Col>
        <Col xs={{ span: 24 }} lg={{ span: 12 }}>
          <Card bordered={false} className="card-stats" title="Top 5 Clientes">
            <div style={{ height: '300px' }}>
              <PieChart data={pieData} options={pieOptions} />
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
